import { useState, useMemo, useEffect, useRef } from 'react';
import { useStore } from '../../store/useStore';
import { useI18n } from '../../i18n';
import BookGrid from './BookGrid';
import BookList from './BookList';
import UploadModal from './UploadModal';
import StatsDashboard from '../Stats/StatsDashboard';
import SyncSettings from '../Sync/SyncSettings';
import LibrarySync from './LibrarySync';
import SearchBar from './SearchBar';
import LibrarySidebar from './LibrarySidebar';
import LanguageSelector from '../Shared/LanguageSelector';
import Footer from '../Shared/Footer';
import FeatureHighlights from '../Shared/FeatureHighlights';
import HelpGuide from '../Shared/HelpGuide';
import OnboardingSlides, { useOnboarding } from '../Onboarding/OnboardingSlides';
import { FiGrid, FiList, FiPlus, FiBarChart2, FiMenu, FiCloud, FiMoon, FiSun, FiSettings, FiHelpCircle, FiUpload } from 'react-icons/fi';

type SortOption = 'recent' | 'title' | 'author' | 'progress' | 'added';

const Library = () => {
  const { books, libraryView, setLibraryView, readerSettings, setTheme, toggleSettings, setCurrentBook, setCurrentView } = useStore();

  // 📖 WIA Reader Bridge — EDD 구매 도서 자동 열기
  useEffect(() => {
    const autoOpenId = sessionStorage.getItem('wia-bridge-auto-open');
    if (!autoOpenId) return;
    sessionStorage.removeItem('wia-bridge-auto-open');
    const store = useStore.getState();
    const targetBook = store.books.find((b: any) => b.id === autoOpenId);
    if (targetBook) {
      console.log('🚀 Auto-open:', autoOpenId, targetBook.title);
      setCurrentBook(targetBook);
      setCurrentView('reader');
    } else {
      console.warn('⚠️ Auto-open: book not found:', autoOpenId);
    }
  }, []);
  const { t } = useI18n();
  const { showOnboarding, completeOnboarding } = useOnboarding();
  const [showUpload, setShowUpload] = useState(false);
  const [droppedFile, setDroppedFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [showStats, setShowStats] = useState(false);
  const [showHelpGuide, setShowHelpGuide] = useState(false);
  const [filterStatus, setFilterStatus] = useState<'all' | 'reading' | 'completed'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<SortOption>('recent');
  const [sidebarFilter, setSidebarFilter] = useState('all');
  const [showSidebar, setShowSidebar] = useState(true);
  const [showSyncSettings, setShowSyncSettings] = useState(false);
  const [showLibrarySync, setShowLibrarySync] = useState(false);

  // ✅ 전역 드래그앤드롭 핸들러
  const dragCounter = useRef(0);
  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    dragCounter.current++;
    if (e.dataTransfer.types.includes('Files')) {
      setIsDragging(true);
    }
  };
  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    dragCounter.current--;
    if (dragCounter.current === 0) {
      setIsDragging(false);
    }
  };
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };
  const handleGlobalDrop = (e: React.DragEvent) => {
    e.preventDefault();
    dragCounter.current = 0;
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file && (file.name.endsWith('.epub') || file.name.endsWith('.pdf'))) {
      setDroppedFile(file);
      setShowUpload(true);
    }
  };

  const filteredBooks = useMemo(() => {
    return books.filter((book) => {
      if (sidebarFilter === 'recent') {
        if (!book.lastRead && book.status !== 'reading') return false;
      } else if (sidebarFilter === 'favorites') {
        if (!book.favorite) return false;
      } else if (sidebarFilter === 'deleted') {
        return false;
      }
      if (filterStatus !== 'all' && book.status !== filterStatus) {
        return false;
      }
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        return (
          book.title.toLowerCase().includes(query) ||
          book.author.toLowerCase().includes(query)
        );
      }
      return true;
    });
  }, [books, filterStatus, searchQuery, sidebarFilter]);

  const sortedBooks = useMemo(() => {
    const sorted = [...filteredBooks];
    switch (sortBy) {
      case 'title':
        return sorted.sort((a, b) => a.title.localeCompare(b.title, 'ko'));
      case 'author':
        return sorted.sort((a, b) => a.author.localeCompare(b.author, 'ko'));
      case 'progress':
        return sorted.sort((a, b) => b.progress - a.progress);
      case 'added':
        return sorted.sort((a, b) =>
          new Date(b.addedDate).getTime() - new Date(a.addedDate).getTime()
        );
      case 'recent':
      default:
        return sorted.sort((a, b) => {
          if (!a.lastRead) return 1;
          if (!b.lastRead) return -1;
          return new Date(b.lastRead).getTime() - new Date(a.lastRead).getTime();
        });
    }
  }, [filteredBooks, sortBy]);

  const recentBooks = sortedBooks.filter((b) => b.status === 'reading').slice(0, 3);
  const otherBooks = sortedBooks.filter((b) => !recentBooks.includes(b));

  return (
    <div
      className="library min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col relative"
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleGlobalDrop}
    >
      {/* 메인 영역: 사이드바 + 콘텐츠 */}
      <div className="flex flex-1 overflow-hidden">
        {showSidebar && (
          <LibrarySidebar
            books={books}
            currentFilter={sidebarFilter}
            onFilterChange={setSidebarFilter}
            onOpenSync={() => setShowLibrarySync(true)}
          />
        )}

        <div className="flex-1 overflow-y-auto bg-white dark:bg-gray-900">
          <div className="container mx-auto px-4 py-8">
            <div className="mb-8">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-4">
                  <button
                    onClick={() => setShowSidebar(!showSidebar)}
                    className="lg:hidden p-2 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors"
                  >
                    <FiMenu size={24} />
                  </button>

                  <div>
                    <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                      {sidebarFilter === 'all' && t('library.myLibrary')}
                      {sidebarFilter === 'recent' && t('library.recentlyRead')}
                      {sidebarFilter === 'favorites' && t('library.favorites')}
                      {sidebarFilter === 'deleted' && t('library.deleted')}
                    </h1>
                    <p className="text-gray-600 dark:text-gray-400">
                      {t('library.totalBooks', { count: books.length })}
                      {searchQuery && ` · ${t('library.searchResults', { count: sortedBooks.length })}`}
                    </p>
                  </div>
                  
                  <div className="flex items-center gap-1 ml-4">
                    <button
                      onClick={() => setShowSyncSettings(true)}
                      className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors text-gray-600 dark:text-gray-400"
                      title={t('settings.cloudSync')}
                    >
                      <FiCloud size={20} />
                    </button>
                    <button
                      onClick={() => {
                        const themes = ['light', 'dark', 'black', 'sepia'] as const;
                        const currentIndex = themes.indexOf(readerSettings.theme as any);
                        const nextIndex = currentIndex === -1 ? 0 : (currentIndex + 1) % themes.length;
                        setTheme(themes[nextIndex]);
                      }}
                      className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors text-gray-600 dark:text-gray-400"
                      title={t('settings.theme')}
                    >
                      {readerSettings.theme === 'dark' ? <FiMoon size={20} /> : <FiSun size={20} />}
                    </button>
                    <button
                      onClick={toggleSettings}
                      className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors text-gray-600 dark:text-gray-400"
                      title={t('settings.title')}
                    >
                      <FiSettings size={20} />
                    </button>
                    <button
                      onClick={() => setShowHelpGuide(true)}
                      className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors text-primary"
                      title={t('help.button') || '기능 가이드'}
                    >
                      <FiHelpCircle size={20} />
                    </button>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <LanguageSelector />
                  <button
                    onClick={() => setShowUpload(true)}
                    className="flex items-center gap-2 px-4 py-2 gradient-primary text-white rounded-lg hover:shadow-lg transition-all"
                  >
                    <FiPlus size={20} />
                    <span className="hidden sm:inline">{t('library.addBook')}</span>
                  </button>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                <div className="flex-1">
                  <SearchBar onSearch={setSearchQuery} />
                </div>

                <button
                  onClick={() => setShowStats(true)}
                  className="flex items-center justify-center gap-2 px-4 py-2.5 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  title={t('library.statistics')}
                >
                  <FiBarChart2 size={20} />
                  <span className="hidden sm:inline">{t('library.statistics')}</span>
                </button>

                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as SortOption)}
                  className="px-4 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
                >
                  <option value="recent">{t('sort.recentlyRead')}</option>
                  <option value="title">{t('sort.byTitle')}</option>
                  <option value="author">{t('sort.byAuthor')}</option>
                  <option value="progress">{t('sort.byProgress')}</option>
                  <option value="added">{t('sort.byDateAdded')}</option>
                </select>

                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value as any)}
                  className="px-4 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
                >
                  <option value="all">{t('filter.all')}</option>
                  <option value="reading">{t('filter.reading')}</option>
                  <option value="completed">{t('filter.completed')}</option>
                </select>

                <div className="flex items-center gap-1 bg-white dark:bg-gray-800 rounded-lg p-1 border border-gray-300 dark:border-gray-600">
                  <button
                    onClick={() => setLibraryView('grid')}
                    className={`p-2 rounded transition-colors ${
                      libraryView === 'grid'
                        ? 'bg-primary text-white'
                        : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                    }`}
                  >
                    <FiGrid size={20} />
                  </button>
                  <button
                    onClick={() => setLibraryView('list')}
                    className={`p-2 rounded transition-colors ${
                      libraryView === 'list'
                        ? 'bg-primary text-white'
                        : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                    }`}
                  >
                    <FiList size={20} />
                  </button>
                </div>
              </div>
            </div>

            {books.length === 0 && (
              <div className="text-center py-20">
                <div className="text-6xl mb-4">📚</div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                  {t('library.emptyLibrary')}
                </h2>
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  {t('library.emptyLibraryDesc')}
                </p>
                <button
                  onClick={() => setShowUpload(true)}
                  className="inline-flex items-center gap-2 px-6 py-3 gradient-primary text-white rounded-lg hover:shadow-lg transition-all"
                >
                  <FiPlus size={24} />
                  <span>{t('library.addFirstBook')}</span>
                </button>
              </div>
            )}

            {recentBooks.length > 0 && (
              <div className="mb-12">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                  {t('library.recentlyReadBooks')}
                </h2>
                {libraryView === 'grid' ? (
                  <BookGrid books={recentBooks} />
                ) : (
                  <BookList books={recentBooks} />
                )}
              </div>
            )}

            {otherBooks.length > 0 && (
              <div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                  {t('library.allBooks')}
                </h2>
                {libraryView === 'grid' ? (
                  <BookGrid books={otherBooks} />
                ) : (
                  <BookList books={otherBooks} />
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 특징 섹션 - Footer 위 */}
      <FeatureHighlights />

      {/* 푸터 - 전체 너비 (사이드바 포함) */}
      <Footer />

      {/* 드래그앤드롭 오버레이 */}
      {isDragging && (
        <div className="fixed inset-0 bg-blue-500/20 backdrop-blur-sm z-40 flex items-center justify-center pointer-events-none">
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-12 shadow-2xl border-4 border-dashed border-blue-500 text-center">
            <FiUpload size={64} className="mx-auto mb-4 text-blue-500 animate-bounce" />
            <p className="text-2xl font-bold text-gray-900 dark:text-white">여기에 놓으세요</p>
            <p className="text-gray-500 mt-2">EPUB / PDF 파일</p>
          </div>
        </div>
      )}

      {/* 모달들 */}
      {showUpload && (
        <UploadModal
          onClose={() => { setShowUpload(false); setDroppedFile(null); }}
          initialFile={droppedFile}
        />
      )}
      {showStats && <StatsDashboard onClose={() => setShowStats(false)} />}
      {showSyncSettings && <SyncSettings onClose={() => setShowSyncSettings(false)} />}
      {showLibrarySync && <LibrarySync isOpen={showLibrarySync} onClose={() => setShowLibrarySync(false)} />}
      {showHelpGuide && <HelpGuide isOpen={showHelpGuide} onClose={() => setShowHelpGuide(false)} />}

      {/* 첫 방문 온보딩 */}
      {showOnboarding && <OnboardingSlides onComplete={completeOnboarding} />}
    </div>
  );
};

export default Library;
